import { ChangeProjectionService, CounterDeltas, LabelingService, Roi, RoiChange } from '@analysis/domain';
import { type RoiChangesRepository, type RoiRepository } from '../../ports';

export interface RemoveAllLabelsPayload {
  rois: Roi[];
}

export interface RemoveAllLabelsResult {
  changes: Map<string, RoiChange | null>;
  updatedRois: Map<string, Roi>;
  counterDeltas: CounterDeltas;
}

export class RemoveAllLabelsUseCase {
  constructor(private readonly roiChanges: RoiChangesRepository, private readonly rois: RoiRepository) {}

  // REVIEW: fetch changes from repository, dont pass as parameter
  public async execute(payload: RemoveAllLabelsPayload): Promise<RemoveAllLabelsResult> {
    const { rois } = payload;

    const previousChanges = await this.roiChanges.getByIds(rois.map((roi) => roi.id));

    const partialUuids = rois
      .filter((r) => {
        if (r.additionalLabelsIncluded || !r.uuid) return false;
        const prior = previousChanges.get(r.id);
        if (prior?.isNew) return false;
        return true;
      })
      .map((r) => r.uuid as string);
    const baselines = partialUuids.length > 0 ? await this.rois.batchGetByIds(partialUuids) : new Map<string, Roi>();

    const hydratedRois = rois.map((roi) => {
      if (roi.additionalLabelsIncluded || !roi.uuid) return roi;
      const prior = previousChanges.get(roi.id);
      if (prior?.isNew) return prior.snapshot ?? roi;
      const baseline = baselines.get(roi.uuid);
      if (!baseline) return roi;
      return prior ? ChangeProjectionService.applyChanges(baseline, prior) : baseline;
    });

    const changes: Map<string, RoiChange | null> = new Map();
    const updatedRois: Map<string, Roi> = new Map();
    const allDeltas: CounterDeltas[] = [];

    const timestamp = Date.now();

    for (const roi of hydratedRois) {
      const previousChange = previousChanges.get(roi.id);
      const result = LabelingService.removeAllLabels(roi);
      const totalChanges = ChangeProjectionService.mergeChanges({
        changes: previousChange ? [previousChange, result.changes] : [result.changes],
        isNew: previousChange?.isNew ?? false,
      });

      allDeltas.push(
        ChangeProjectionService.computeCounterDeltas(
          Array.from(roi.labels.keys()),
          Array.from(result.roi.labels.keys()),
          result.changes.labelsAdded,
          result.changes.labelsRemoved
        )
      );

      updatedRois.set(roi.id, result.roi);

      changes.set(
        roi.id,
        totalChanges === null
          ? null
          : {
              id: roi.id,
              sampleId: roi.sampleId,
              assetId: roi.assetId,
              creatorId: roi.creatorId,
              timestamp,
              isNew: previousChange?.isNew ?? false,
              labelsAdded: totalChanges.labelsAdded,
              labelsRemoved: totalChanges.labelsRemoved,
              snapshot: previousChange?.isNew ? result.roi : previousChange?.snapshot,
            }
      );
    }

    await this.roiChanges.upsertRoiChanges(changes);

    return { changes, updatedRois, counterDeltas: ChangeProjectionService.mergeCounterDeltas(allDeltas) };
  }
}
